import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import type { WizardConfig } from '../types/wizard'

export default function CheckoutPage() {
  const navigate = useNavigate()
  const [config, setConfig] = useState<WizardConfig | null>(null)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Load configuration from session storage
    const savedConfig = sessionStorage.getItem('wizardConfig')
    if (savedConfig) {
      setConfig(JSON.parse(savedConfig))
    } else {
      // No configuration found, redirect to configure page
      navigate('/configure')
    }
  }, [navigate])

  const handleCheckout = async () => {
    if (!config || !acceptedTerms) return

    setIsLoading(true)
    setError(null)

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://hellabots-api.azurewebsites.net'
      const response = await fetch(`${apiUrl}/storefront/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          config,
          email: config.email,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()

        // Special handling for 409 (client ID taken)
        if (response.status === 409) {
          setError(`The name "${config.agentName}" is already taken. Please go back and add a middle initial, location, or team name (e.g. "${config.agentName} - Portland" or "${config.agentName} Realty").`)
        } else {
          setError(errorData.message || errorData.error || 'Failed to create checkout session')
        }
        setIsLoading(false)
        return
      }

      const { url } = await response.json()

      // Redirect to Stripe Checkout
      window.location.href = url
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      setIsLoading(false)
    }
  }

  if (!config) {
    return null // Will redirect in useEffect
  }

  const setupFee = 297
  const monthlyFee = 297
  const firstCharge = setupFee + monthlyFee

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <section className="section bg-white flex-1">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Complete Your Order
            </h1>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Configuration Summary */}
              <div className="md:col-span-2 space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Configuration Summary
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Agent Information</p>
                      <p className="text-gray-900">{config.agentName}</p>
                      <p className="text-gray-600 text-sm">{config.email}</p>
                      <p className="text-gray-600 text-sm">{config.phone}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-500">Calendly URL</p>
                      <p className="text-gray-900 text-sm truncate">{config.calendlyUrl}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-500">Cities</p>
                      <p className="text-gray-900">{config.cities.join(', ')}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-500">Price Ranges</p>
                      <p className="text-gray-900">{config.priceRanges.join(', ')}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-500">Branding</p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded border border-gray-300"
                            style={{ backgroundColor: config.primaryColor }}
                          />
                          <span className="text-sm text-gray-600">{config.primaryColor}</span>
                        </div>
                        <span className="text-gray-400">•</span>
                        <span className="text-sm text-gray-600">
                          Bot Name: {config.botNamePreference || 'Random'}
                        </span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-500">Client ID</p>
                      <p className="text-gray-900 font-mono text-sm">{config.clientId}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/configure')}
                    className="mt-6 text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    ← Edit Configuration
                  </button>
                </div>

                {/* Terms and Conditions */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Terms and Conditions
                  </h2>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-600">
                      I agree to the{' '}
                      <a href="#" className="text-primary-600 hover:text-primary-700 underline">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-primary-600 hover:text-primary-700 underline">
                        Privacy Policy
                      </a>
                      . I understand that my subscription will automatically renew at ${monthlyFee}/month
                      until cancelled.
                    </label>
                  </div>
                </div>
              </div>

              {/* Pricing Summary */}
              <div className="md:col-span-1">
                <div className="bg-gradient-to-br from-primary-50 to-white border border-primary-200 rounded-lg p-6 sticky top-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Order Summary
                  </h2>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-gray-600">
                      <span>Setup Fee</span>
                      <span className="font-medium">${setupFee}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>First Month</span>
                      <span className="font-medium">${monthlyFee}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-3 flex justify-between text-lg font-semibold text-gray-900">
                      <span>Total Due Today</span>
                      <span>${firstCharge}</span>
                    </div>
                  </div>

                  <div className="bg-primary-100 rounded-lg p-4 mb-6">
                    <p className="text-sm text-primary-900 font-medium mb-2">
                      Then ${monthlyFee}/month
                    </p>
                    <p className="text-xs text-primary-700">
                      Cancel anytime from your Stripe customer portal
                    </p>
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}

                  <button
                    onClick={handleCheckout}
                    disabled={!acceptedTerms || isLoading}
                    className="w-full btn-primary disabled:bg-gray-300 disabled:cursor-not-allowed disabled:text-gray-500"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      `Proceed to Payment - $${firstCharge}`
                    )}
                  </button>

                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    Secure payment by Stripe
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
